// window.xwAnalytics: the one browser surface apps use for attribution, identity and
// contract-checked tracking (analytics #15, ADR #952). Consent is read from the
// xw_consent_* cookies that the blocking bootstrap keeps in sync with OneTrust.
import contract from '../../core/contract/segment-events.json' with { type: 'json' };
import { ATTRIBUTION_FIELDS, resolveUserId, validateTrack, type EventName } from '../../core/src/index.js';

export type AttributionMode = 'direct' | 'touch';
export type Properties = Record<string, unknown>;

export interface TrackResult {
  sent: boolean;
  errors: string[];
}

export interface XwAnalytics {
  getAttribution(mode?: AttributionMode): Record<string, string>;
  identify(auth0Subject: string, traits?: Properties): boolean;
  track(event: EventName, properties?: Properties, options?: { groupId?: string }): TrackResult;
  getAnonymousId(): string | null;
  reset(): void;
}

declare global {
  interface Window {
    xwAnalytics: XwAnalytics;
  }
}

interface SegmentLike {
  initialized?: boolean;
  ready?(cb: () => void): void;
  identify(userId: string, traits: Properties): void;
  track(event: string, properties: Properties, options?: Properties): void;
  user?(): { anonymousId(): string | null };
  reset?(): void;
}

const PREFIX = 'xw_';
const FIRST_TOUCH_PREFIX = 'xw_ft_';
// Ad-cookie snapshots are not touch-scoped, so they have no first-touch copy.
const SNAPSHOT_FIELDS = ['fbc', 'fbp', 'gcl_aw', 'gcl_gb', 'uet_msclkid'];
const ATTRIBUTION_MODE = new Map(
  (contract.events as Array<{ name: string; attribution?: AttributionMode }>).map((e) => [e.name, e.attribution]),
);
const READY_TIMEOUT_MS = 30000;
const READY_POLL_MS = 250;

function readCookie(name: string): string | null {
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    if (part.indexOf(name + '=') !== 0) continue;
    try {
      return decodeURIComponent(part.slice(name.length + 1));
    } catch (_) {
      return null;
    }
  }
  return null;
}

function consentGranted(cookie: string): boolean {
  return readCookie(cookie) === 'granted';
}

export function getAttribution(mode: AttributionMode = 'direct'): Record<string, string> {
  const result: Record<string, string> = {};
  if (!consentGranted('xw_consent_marketing')) return result;
  for (const field of ATTRIBUTION_FIELDS) {
    const last = readCookie(PREFIX + field);
    if (mode === 'direct') {
      if (last) result[field] = last;
      continue;
    }
    if (last) result[`last_touch_${field}`] = last;
    const first = SNAPSHOT_FIELDS.includes(field) ? null : readCookie(FIRST_TOUCH_PREFIX + field);
    if (first) result[`first_touch_${field}`] = first;
  }
  return result;
}

// Calls wait for the real analytics.js; the snippet stub alone is not enough to read ids.
const pending: Array<(a: SegmentLike) => void> = [];
let waiting = false;

function whenReady(fn: (a: SegmentLike) => void): void {
  pending.push(fn);
  if (waiting) return;
  waiting = true;
  const started = Date.now();
  const flush = () => {
    waiting = false;
    const a = (window as any).analytics as SegmentLike;
    for (const call of pending.splice(0)) call(a);
  };
  const poll = () => {
    const a = (window as any).analytics as SegmentLike | undefined;
    if (a && typeof a.ready === 'function') return a.ready(flush);
    if (Date.now() - started < READY_TIMEOUT_MS) setTimeout(poll, READY_POLL_MS);
    else {
      waiting = false;
      pending.length = 0;
    }
  };
  poll();
}

export function identify(auth0Subject: string, traits: Properties = {}): boolean {
  if (!consentGranted('xw_consent_marketing')) return false;
  const user = resolveUserId(auth0Subject);
  if (!user) return false;
  whenReady((a) => a.identify(user.userId, { ...traits, auth0Id: user.auth0Id }));
  return true;
}

export function track(event: EventName, properties: Properties = {}, options: { groupId?: string } = {}): TrackResult {
  if (!consentGranted('xw_consent_analytics')) return { sent: false, errors: ['consent: C0002 not granted'] };
  const props = ATTRIBUTION_MODE.get(event) === 'direct' ? { ...getAttribution('direct'), ...properties } : properties;
  const errors = validateTrack(event, props, options.groupId);
  if (errors.length) {
    console.warn(`xwAnalytics: "${event}" rejected by the contract`, errors);
    return { sent: false, errors };
  }
  const context = options.groupId ? { context: { groupId: options.groupId } } : undefined;
  whenReady((a) => a.track(event, props, context));
  return { sent: true, errors };
}

export function getAnonymousId(): string | null {
  const a = (window as any).analytics as SegmentLike | undefined;
  try {
    const id = a?.initialized && a.user ? a.user().anonymousId() : null;
    if (id) return id;
  } catch (_) {}
  // analytics.js stores the id JSON-encoded.
  const raw = readCookie('ajs_anonymous_id');
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed : raw;
  } catch (_) {
    return raw;
  }
}

// Clears the Segment user on logout so the next person on this browser starts fresh.
// Not consent-gated: forgetting an identity is always allowed.
export function reset(): void {
  whenReady((a) => a.reset?.());
}

export const xwAnalytics: XwAnalytics = { getAttribution, identify, track, getAnonymousId, reset };
